import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { PeriodFilter } from '../components/PeriodFilter';
import { TrendingUp, TrendingDown, DollarSign, ShoppingCart, Receipt, ChevronDown, ChevronRight, Users, Calendar } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (num) => {
  if (num === null || num === undefined) return '0';
  return Number(num).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

const ProfitTrackingPage = () => {
  const [data, setData] = useState(null);
  const [customerData, setCustomerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [periodFilter, setPeriodFilter] = useState({ startDate: '', endDate: '', label: '' });
  const [expandedDays, setExpandedDays] = useState({});
  const [view, setView] = useState('date'); // 'date' | 'customer'

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (periodFilter.startDate) params.append('start_date', periodFilter.startDate);
      if (periodFilter.endDate) params.append('end_date', periodFilter.endDate);
      const [byDate, byCust] = await Promise.all([
        axios.get(`${API_URL}/reports/profit-tracking?${params.toString()}`),
        axios.get(`${API_URL}/reports/profit-tracking/by-customer?${params.toString()}`),
      ]);
      setData(byDate.data);
      setCustomerData(byCust.data);
    } catch (error) {
      toast.error('Failed to load profit data');
    } finally {
      setLoading(false);
    }
  }, [periodFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleDay = (date) => setExpandedDays(prev => ({ ...prev, [date]: !prev[date] }));

  if (loading || !data || !customerData) {
    return <div className="flex items-center justify-center h-64"><div className="text-muted-foreground">Loading profit data...</div></div>;
  }

  const { summary, daily_breakdown } = data;
  const dailyDesc = [...daily_breakdown].sort((a, b) => b.date.localeCompare(a.date));
  const custSummary = customerData.summary;
  const custRows = customerData.rows;

  return (
    <div className="space-y-6" data-testid="profit-tracking-page">
      <div>
        <h1 className="text-3xl font-heading font-semibold text-foreground">Profit Tracking</h1>
        <p className="text-muted-foreground">Daily revenue vs costs breakdown by client</p>
      </div>

      <PeriodFilter onFilterChange={setPeriodFilter} />

      {/* View toggle */}
      <div className="flex gap-1 bg-muted/40 rounded-lg p-1 w-fit" data-testid="view-toggle">
        <button
          onClick={() => setView('date')}
          className={`px-4 py-1.5 rounded-md text-sm font-medium transition ${view === 'date' ? 'bg-white shadow-sm text-foreground' : 'text-muted-foreground'}`}
          data-testid="view-date"
        ><Calendar className="w-4 h-4 inline mr-1" />By date</button>
        <button
          onClick={() => setView('customer')}
          className={`px-4 py-1.5 rounded-md text-sm font-medium transition ${view === 'customer' ? 'bg-white shadow-sm text-foreground' : 'text-muted-foreground'}`}
          data-testid="view-customer"
        ><Users className="w-4 h-4 inline mr-1" />By customer <span className="text-xs text-muted-foreground ml-1">(Gross Profit)</span></button>
      </div>

      {view === 'customer' ? (
        <CustomerBreakdown summary={custSummary} rows={custRows} />
      ) : (
        <DateBreakdown summary={summary} dailyDesc={dailyDesc} expandedDays={expandedDays} toggleDay={toggleDay} />
      )}
    </div>
  );
};

const DateBreakdown = ({ summary, dailyDesc, expandedDays, toggleDay }) => (
  <>
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-white rounded-xl border border-border shadow-sm p-5" data-testid="total-revenue-card">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Total Revenue</p>
              <p className="text-lg font-bold text-emerald-600">RWF {fmt(summary.total_revenue)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-border shadow-sm p-5" data-testid="total-purchases-card">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <ShoppingCart className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Total Purchases</p>
              <p className="text-lg font-bold text-blue-600">RWF {fmt(summary.total_purchases)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-border shadow-sm p-5" data-testid="total-expenses-card">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
              <Receipt className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Total Expenses</p>
              <p className="text-lg font-bold text-orange-600">RWF {fmt(summary.total_expenses)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-border shadow-sm p-5" data-testid="total-costs-card">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
              <TrendingDown className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Total Costs</p>
              <p className="text-lg font-bold text-red-600">RWF {fmt(summary.total_costs)}</p>
            </div>
          </div>
        </div>

        <div className={`rounded-xl border shadow-sm p-5 ${summary.net_profit >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`} data-testid="net-profit-card">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${summary.net_profit >= 0 ? 'bg-emerald-200' : 'bg-red-200'}`}>
              <TrendingUp className={`w-5 h-5 ${summary.net_profit >= 0 ? 'text-emerald-700' : 'text-red-700'}`} />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Net Profit</p>
              <p className={`text-lg font-bold ${summary.net_profit >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                RWF {fmt(Math.abs(summary.net_profit))}
                {summary.net_profit < 0 && <span className="text-xs ml-1">(Loss)</span>}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Daily Breakdown Table */}
      <div className="bg-white rounded-xl border border-border shadow-sm overflow-hidden" data-testid="daily-breakdown">
        <div className="px-6 py-4 border-b border-border bg-muted/30">
          <h2 className="text-lg font-semibold">Daily Breakdown</h2>
          <p className="text-sm text-muted-foreground">{summary.days_count} days, {summary.total_orders} orders</p>
        </div>

        {dailyDesc.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground">No data found for the selected period.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50 border-b border-border">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-foreground w-8"></th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Date</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Clients</th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-foreground">Orders</th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-emerald-700">Revenue</th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-blue-700">Purchases</th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-orange-700">Expenses</th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-foreground">Total Costs</th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-foreground">Profit</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {dailyDesc.map((day) => {
                  const isExpanded = expandedDays[day.date];
                  return (
                    <React.Fragment key={day.date}>
                      <tr
                        className="hover:bg-muted/30 cursor-pointer transition-colors"
                        onClick={() => toggleDay(day.date)}
                        data-testid={`day-row-${day.date}`}
                      >
                        <td className="px-4 py-3 text-sm">
                          {isExpanded ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                        </td>
                        <td className="px-4 py-3 text-sm font-medium">{new Date(day.date + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}</td>
                        <td className="px-4 py-3 text-sm">
                          {day.customers.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {day.customers.map((c) => (
                                <span key={c} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">{c}</span>
                              ))}
                            </div>
                          ) : <span className="text-muted-foreground">—</span>}
                        </td>
                        <td className="px-4 py-3 text-sm text-right">{day.orders_count}</td>
                        <td className="px-4 py-3 text-sm text-right font-semibold text-emerald-600">RWF {fmt(day.revenue)}</td>
                        <td className="px-4 py-3 text-sm text-right text-blue-600">{day.purchases_total > 0 ? `RWF ${fmt(day.purchases_total)}` : '—'}</td>
                        <td className="px-4 py-3 text-sm text-right text-orange-600">{day.expenses_total > 0 ? `RWF ${fmt(day.expenses_total)}` : '—'}</td>
                        <td className="px-4 py-3 text-sm text-right font-medium text-red-600">{day.total_costs > 0 ? `RWF ${fmt(day.total_costs)}` : '—'}</td>
                        <td className={`px-4 py-3 text-sm text-right font-bold ${day.profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          RWF {fmt(Math.abs(day.profit))}{day.profit < 0 && ' (Loss)'}
                        </td>
                      </tr>

                      {/* Expanded details */}
                      {isExpanded && (
                        <tr>
                          <td colSpan={9} className="bg-muted/20 px-6 py-4">
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                              {/* Orders detail */}
                              <div>
                                <h4 className="text-sm font-semibold text-emerald-700 mb-2">Orders ({day.orders_count})</h4>
                                {day.order_details.length === 0 ? (
                                  <p className="text-xs text-muted-foreground">No orders</p>
                                ) : (
                                  <div className="space-y-1">
                                    {day.order_details.map((o) => (
                                      <div key={o.id || `${o.customer}-${o.total}`} className="flex justify-between text-xs bg-white p-2 rounded border">
                                        <span className="font-medium">{o.customer} <span className="text-muted-foreground">({o.items_count} items)</span></span>
                                        <span className="font-semibold text-emerald-600">RWF {fmt(o.total)}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>

                              {/* Purchases detail */}
                              <div>
                                <h4 className="text-sm font-semibold text-blue-700 mb-2">Purchases ({day.purchases_count})</h4>
                                {day.purchase_details.length === 0 ? (
                                  <p className="text-xs text-muted-foreground">No purchases</p>
                                ) : (
                                  <div className="space-y-1">
                                    {day.purchase_details.map((p) => (
                                      <div key={p.id || `${p.supplier}-${p.total}`} className="flex justify-between text-xs bg-white p-2 rounded border">
                                        <span className="font-medium">{p.supplier}</span>
                                        <span className="font-semibold text-blue-600">RWF {fmt(p.total)}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>

                              {/* Expenses detail */}
                              <div>
                                <h4 className="text-sm font-semibold text-orange-700 mb-2">Expenses ({day.expenses_count})</h4>
                                {day.expense_details.length === 0 ? (
                                  <p className="text-xs text-muted-foreground">No expenses</p>
                                ) : (
                                  <div className="space-y-1">
                                    {day.expense_details.map((e) => (
                                      <div key={e.id || `${e.category}-${e.description}-${e.total}`} className="flex justify-between text-xs bg-white p-2 rounded border">
                                        <span className="font-medium">{e.category}: {e.description}</span>
                                        <span className="font-semibold text-orange-600">RWF {fmt(e.total)}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
              <tfoot className="bg-muted/50 border-t-2 border-border">
                <tr className="font-semibold">
                  <td className="px-4 py-3" colSpan={2}></td>
                  <td className="px-4 py-3 text-sm">TOTALS</td>
                  <td className="px-4 py-3 text-sm text-right">{summary.total_orders}</td>
                  <td className="px-4 py-3 text-sm text-right text-emerald-700">RWF {fmt(summary.total_revenue)}</td>
                  <td className="px-4 py-3 text-sm text-right text-blue-700">RWF {fmt(summary.total_purchases)}</td>
                  <td className="px-4 py-3 text-sm text-right text-orange-700">RWF {fmt(summary.total_expenses)}</td>
                  <td className="px-4 py-3 text-sm text-right text-red-700">RWF {fmt(summary.total_costs)}</td>
                  <td className={`px-4 py-3 text-sm text-right font-bold ${summary.net_profit >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                    RWF {fmt(Math.abs(summary.net_profit))}{summary.net_profit < 0 && ' (Loss)'}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
  </>
);

const CustomerBreakdown = ({ summary, rows }) => (
  <div className="space-y-4" data-testid="customer-breakdown">
    {/* Summary cards */}
    <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
      <SummaryCard label="Customers" value={summary.customer_count} tone="text-primary" bg="bg-primary/10" />
      <SummaryCard label="Total Revenue" value={`RWF ${fmt(summary.total_revenue)}`} tone="text-emerald-700" bg="bg-emerald-50" />
      <SummaryCard label="Total COGS" value={`RWF ${fmt(summary.total_cogs)}`} tone="text-orange-700" bg="bg-orange-50" />
      <SummaryCard label={`Gross Profit · ${summary.avg_gross_margin_pct}% margin`} value={`RWF ${fmt(summary.total_gross_profit)}`} tone="text-emerald-800" bg="bg-emerald-100" />
    </div>

    <div className="bg-white rounded-xl border border-border shadow-sm overflow-hidden">
      <div className="px-5 py-3 border-b border-border bg-muted/30">
        <h2 className="text-lg font-semibold">Gross profit by customer</h2>
        <p className="text-xs text-muted-foreground">Sorted by gross profit (highest first) · use these numbers for commissions & targets</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="px-4 py-3 text-left font-medium">Customer</th>
              <th className="px-4 py-3 text-right font-medium">Orders</th>
              <th className="px-4 py-3 text-right font-medium text-emerald-700">Revenue</th>
              <th className="px-4 py-3 text-right font-medium text-orange-700">COGS</th>
              <th className="px-4 py-3 text-right font-medium">Gross Profit</th>
              <th className="px-4 py-3 text-right font-medium">Margin %</th>
              <th className="px-4 py-3 text-left font-medium">Last order</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border" data-testid="customer-rows">
            {rows.length === 0 ? (
              <tr><td colSpan={7} className="p-10 text-center text-muted-foreground">No orders in the selected period.</td></tr>
            ) : rows.map((r) => (
              <tr key={r.customer_id || r.customer_name} className="hover:bg-muted/30" data-testid={`customer-row-${r.customer_id || r.customer_name}`}>
                <td className="px-4 py-3 font-medium">{r.customer_name}</td>
                <td className="px-4 py-3 text-right">{r.orders_count}</td>
                <td className="px-4 py-3 text-right text-emerald-700">RWF {fmt(r.revenue)}</td>
                <td className="px-4 py-3 text-right text-orange-700">RWF {fmt(r.cogs)}</td>
                <td className={`px-4 py-3 text-right font-bold ${r.gross_profit >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>RWF {fmt(r.gross_profit)}</td>
                <td className="px-4 py-3 text-right">{r.gross_margin_pct}%</td>
                <td className="px-4 py-3 text-xs text-muted-foreground">{r.last_order_at ? new Date(r.last_order_at).toLocaleDateString('en-GB') : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </div>
);

const SummaryCard = ({ label, value, tone, bg }) => (
  <div className={`rounded-xl border border-border shadow-sm p-4 ${bg}`}>
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className={`text-lg font-bold mt-1 ${tone}`}>{value}</div>
  </div>
);

export default ProfitTrackingPage;
